import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Button, Card, Flex, Typography } from 'antd';

import isEmpty from 'lodash.isempty';

import { isShapeInsideBoundary } from '../../../helpers/map';
import { hasPermission } from '../../../helpers/user';

import { MAPS_TAB } from '../../../constants/map';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';
import { PLAN_TYPE } from '../../../constants/subscription';

import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { deleteFarm, fetchFarm, updateFarm } from '../../../redux/actions/farm';
import { fetchGrazingData, fetchGrazingHeatmapStyles } from '../../../redux/actions/grazing';

import AppModal from '../../../components/AppModal';
import CancelSaveButtons from '../../../components/CancelSaveButtons';
import CitySelector from '../../../components/CitySelector';
import LoadingBar from '../../../components/LoadingBar';
import Map from '../../../components/Maps/MapFarm';
import { DEFAULT_SHAPE_COLOR, TYPE } from '../../../components/Maps/constants';
import PrevNextButtons from '../../../components/PrevNextButtons';
import { errorToastHandler, successToastHandler, warnToastHandler } from '../../../components/action_notifier';
import UnsavedChangesModal from '../../../components/ui/UnsavedChangesModal';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';
import AppDropdown from '../../ui-components/AppDropdown';

import FarmEditForm from './FarmEditForm';
import FarmHistory from './FarmHistory';
import './styles.css';

const { Title, Text } = Typography;
const FARM_MODEL = {
  address: '',
  animals_count: '',
  circumference: '',
  description: '',
  full_address: '',
  geofence_id: '',
  geofences: [],
  id: null,
  name: '',
  rules: [],
  size: ''
};

export default function Farm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const formRef = useRef(null);

  const { data: grazing } = useSelector(state => state.grazing);
  const { farm, loading } = useSelector(state => state.farm);
  const { myPlan } = useSelector(state => state.subscription);
  const { permissions } = useSelector(state => state.user);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [address, setAddress] = useState(null);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    color: DEFAULT_SHAPE_COLOR,
    delete_modal: false,
    editing: false,
    farm: { ...FARM_MODEL },
    farmAddress: '',
    farm_edit: { ...FARM_MODEL },
    originalGeoJson: null,
    selected_geofences: []
  });

  useEffect(() => {
    getFarm();
    fetchHeatmapStyles();
  }, []);

  useEffect(() => {
    if (!farm) return;

    getGrazingData();

    setAddress({ country: farm.address?.country, city: farm.address?.city });
    setState(prevState => ({
      ...prevState,
      color: farm.master_geofence?.color,
      farm: structuredClone(farm),
      farmAddress: `${farm.address?.country},${farm.address?.city}`,
      farm_edit: { ...structuredClone(farm), geo_json: farm.master_geofence?.geo_json },
      selected_geofences: farm.geofences
        .filter(gf => !gf.is_master)
        .map(gf => ({ value: gf.id, label: gf.name, geofence: gf })),
      originalGeoJson: farm.master_geofence
    }));
  }, [farm]);

  const toggleModal = useCallback(
    modal => {
      setState(prevState => ({ ...prevState, [modal]: !prevState[modal] }));
    },
    [state.delete_modal]
  );

  const navigateToThePrevPage = useCallback(() => {
    history.push(`/farms/${state.farm?.prev_id}`);
  }, [state.farm?.prev_id]);

  const navigateToTheNextPage = useCallback(() => {
    history.push(`/farms/${state.farm?.next_id}`);
  }, [state.farm?.next_id]);

  const navigateToTrackingPage = useCallback(() => {
    history.push(`/tracking/${state.farm?.animals?.at(0)?.id}`);
  }, [state.farm?.animals]);

  const changeColor = useCallback(color => {
    setState(prevState => ({ ...prevState, color }));
  }, []);

  async function fetchHeatmapStyles() {
    try {
      await dispatch(fetchGrazingHeatmapStyles()).unwrap();
    } catch (error) {
      console.error(error);
    }
  }

  async function getFarm() {
    if (!id) return;

    try {
      await dispatch(fetchFarm(id)).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function getGrazingData() {
    if (!farm) return;

    const rotationIds = farm?.geofences?.map(g => (g.currentRotation ? g.currentRotation.id : g.lastRotation?.id));

    if (!rotationIds?.length) return;

    return dispatch(fetchGrazingData({ rotationIds }));
  }

  async function saveFarm() {
    const values = await formRef.current?.validateFields();
    const fullAddress = {
      city: address?.city,
      country: address?.country,
      house_number: values.house_number,
      postal_code: values.postal_code,
      street: values.street
    };
    const updatedFarm = { ...state.farm_edit, ...values, address: fullAddress };
    const masterGeoJson = updatedFarm?.master_geofence?.geo_json;

    try {
      if (isEmpty(masterGeoJson)) {
        warnToastHandler(messages.MASTER_GEOFENCE_REQUIRED);

        return;
      }

      const boundary = masterGeoJson;
      const geofences = state.farm.geofences?.filter(g => !g.is_master).map(g => g.geo_json);

      if (!isShapeInsideBoundary(boundary, geofences)) {
        warnToastHandler(messages.GEOFENCE_OUTSIDE_MASTER);

        return;
      }

      const { id, address, name, description, holding_numbers } = updatedFarm;

      await dispatch(
        updateFarm({
          id,
          data: {
            id,
            address,
            name,
            description,
            color: state.color,
            holding_numbers: holding_numbers ? [String(holding_numbers)] : [],
            master_geofence: masterGeoJson,
            geofence_ids: state.selected_geofences.map(sg => sg.value).filter(Boolean)
          }
        })
      ).unwrap();
      await getFarm();
      setState(prevState => ({ ...prevState, editing: false }));
      setIsFormDirty(false);
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_UPDATE_DATA);
    }

    successToastHandler(messages.UPDATED);
  }

  async function removeFarm() {
    if (!id) return;

    try {
      await dispatch(deleteFarm(id)).unwrap();
      successToastHandler(messages.ARCHIVED);
      history.push(`/maps?tab=${MAPS_TAB.FARM}`);
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_DELETE_DATA);
    }
  }

  const onChange = (value, field) => {
    setState(prevState => ({ ...prevState, [field]: value }));
  };

  const handleMasterGeofenceGeoJsonChange = geo_json => {
    setIsFormDirty(true);
    setState(prevState => ({
      ...prevState,
      farm_edit: {
        ...prevState.farm_edit,
        master_geofence: { ...prevState.farm.master_geofence, geo_json }
      },
      farm: { ...prevState.farm, master_geofence: { ...farm.master_geofence, geo_json } }
    }));
  };

  const deleteGeoJson = () => {
    setIsFormDirty(true);
    setState(prevState => ({
      ...prevState,
      farm_edit: { ...prevState.farm_edit, master_geofence: { ...farm.master_geofence, geo_json: null } }
    }));
    setSubmittable(false);
    warnToastHandler(messages.MASTER_GEOFENCE_REQUIRED);
  };

  const actions = useMemo(() => {
    const availableActions = [];

    if (hasPermission([PERMISSIONS.FARMS.UPDATE_OWN], permissions)) {
      availableActions.push({
        label: (
          <Button color="default" size="small" variant="link" onClick={() => onChange(true, 'editing')}>
            Edit farm
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.FARMS.DELETE_OWN], permissions)) {
      availableActions.push({
        label: (
          <Button color="default" size="small" variant="link" onClick={() => toggleModal('delete_modal')}>
            Archive farm
          </Button>
        )
      });
    }

    return availableActions;
  }, [toggleModal, permissions]);

  const validate = () => {
    const farmName = formRef?.current?.getFieldValue('name');

    if (typeof farmName === 'string' && farmName.trim() !== '') {
      setSubmittable(true);
    }
  };

  const breadcrumbItems = [
    {
      title: <Link to={`/maps?tab=${MAPS_TAB.FARM}`}>List of Farms</Link>
    },
    {
      title: `${state.editing ? 'Edit ' : ''}Farm ${state.farm?.name}`
    }
  ];

  const onCancelEdit = (value, field) => {
    setState(prev => ({
      ...prev,
      farmAddress: farm?.address?.city
        ? `${farm?.address?.country},${farm?.address?.city}`
        : `${farm?.address?.country}`,
      farm_edit: structuredClone(prev.farm),
      [field]: value
    }));
    setAddress({ country: farm?.address?.country, city: farm?.address?.city });
  };

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (submittable) {
          await saveFarm();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        setIsFormDirty(false);
        onCancelEdit(false, 'editing');
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : onCancelEdit(false, 'editing');
  }, [isFormDirty]);

  const changeAddress = useCallback((_, option) => {
    const { country, city } = option ?? address;

    if (`${address?.country},${address?.city}` !== `${country},${city}`) setIsFormDirty(true);

    setState(prev => ({
      ...prev,
      farmAddress: `${city}, ${country}` ?? `${address?.city}, ${address?.country}`
    }));
    setAddress({ country, city });
  }, []);

  const isAddressChanged = state.farmAddress !== `${farm?.address?.country},${farm?.address?.city}`;
  const isFarmDrawn = !isEmpty(state.farm_edit.master_geofence?.geo_json);

  return (
    <>
      {loading && !farm ? (
        <LoadingBar fullscreen={true} />
      ) : (
        <>
          {NavigationPrompt}
          <Flex gap="small" vertical={false} justify="space-between" wrap>
            <Flex vertical={true}>
              <Title level={4}>
                {state.editing && 'Edit '}Farm {state.farm?.name}
              </Title>

              <AppBreadcrumb items={breadcrumbItems} />
            </Flex>
            <Flex gap="small">
              {!state.editing && <AppDropdown label="Actions" items={actions} />}
              {state.editing && (
                <CancelSaveButtons
                  disabled={!submittable || isEmpty(state.farm_edit?.master_geofence?.geo_json)}
                  handleCancel={handleCancel}
                  handleSave={saveFarm}
                />
              )}

              <PrevNextButtons
                nextId={state.farm?.next_id}
                prevId={state.farm?.prev_id}
                handlePrev={navigateToThePrevPage}
                handleNext={navigateToTheNextPage}
              />
            </Flex>
          </Flex>

          <Card className="mb-2">
            <Text type="secondary">Mark the size and borders of the farm.</Text>
            <Flex align="center" className="mb-2 flex-wrap flex-sm-nowrap" gap={'0 10px'}>
              <Text strong className="flex-fill text-nowrap">
                Closest town or city
              </Text>
              <CitySelector
                className="flex-fill w-100"
                defaultValue={`${address?.city}, ${address?.country}`}
                disabled={!state.editing}
                handleClear={changeAddress}
                handleSelect={changeAddress}
              />
            </Flex>

            <Map
              className={state.editing ? 'editing' : ''}
              color={state.color}
              editing={state.editing}
              {...(state.editing && (isAddressChanged || !isFarmDrawn) && { defaultPosition: state.farmAddress })}
              farm={state.farm_edit}
              grazing={grazing}
              isNew={false}
              loading={loading}
              sites={state.farm?.sites}
              type={TYPE.FARM}
              editFence={handleMasterGeofenceGeoJsonChange}
              handleCancelEditing={handleCancel}
              handleSaveEditing={saveFarm}
              handleDelete={deleteGeoJson}
              onCreateGeofence={handleMasterGeofenceGeoJsonChange}
              setAnimal={navigateToTrackingPage}
              setSubmittable={validate}
            />

            {farm && (
              <FarmEditForm
                color={state.color}
                editing={state.editing}
                farm={state.farm_edit}
                formRef={formRef}
                handleColorChange={changeColor}
                onChange={setIsFormDirty}
                setSubmittable={setSubmittable}
              />
            )}
          </Card>

          {state.editing && (
            <Flex justify="end" className="mb-2">
              <CancelSaveButtons
                disabled={!submittable || isEmpty(state.farm_edit?.master_geofence?.geo_json)}
                handleCancel={handleCancel}
                handleSave={saveFarm}
              />
            </Flex>
          )}

          {![PLAN_TYPE.ADVANCED, PLAN_TYPE.BASIC, PLAN_TYPE.TRIAL].includes(myPlan?.type) && (
            <FarmHistory farmId={id} />
          )}
        </>
      )}

      <AppModal
        isOpen={state.delete_modal}
        confirmButtonColor="danger"
        confirmButtonText="Archive"
        title="Archive farm"
        handleCancel={() => toggleModal('delete_modal')}
        handleConfirm={removeFarm}>
        <div className="py-4">{messages.ARCHIVE_CONFIRMATION('farm')}</div>
      </AppModal>

      <UnsavedChangesModal
        isOpen={modalProps.visible}
        handleCancel={modalProps.onCancel}
        handleConfirm={modalProps.onSave}
        handleDiscard={modalProps.onDiscard}
      />
    </>
  );
}
